import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import styled from 'styled-components';
import { Button, Card, TextField } from 'src/components/designSystem';
import {
  CritiquePublicGroup,
  useCritiqueDiscover,
} from 'src/hooks/useCritiqueGroups';
import { MediaQuery } from 'src/constants/size';
import { BookSpine } from 'src/components/critiqueGroups/bookSpine';
import LoadMore from 'src/components/lists/loadMore';
import {
  CritiqueEmptyState,
  CritiqueErrorState,
  CritiqueLoadingState,
} from 'src/components/critiqueGroups/critiqueStates';

/**
 * The public discovery catalog — `listed` critique groups a logged-in author
 * can browse and ask to join. Reads only the public projection; a card links to
 * the group's preview, where the actual "Request to join" lives.
 */

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const Grid = styled.div`
  margin-top: 22px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  ${MediaQuery.phone} {
    grid-template-columns: 1fr;
  }
`;

const CardContent = styled.div`
  display: flex;
  gap: 14px;
`;

const SearchForm = styled.form`
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: min(100%, 360px);

  .br-field {
    flex: 1 1 220px;
  }
`;

const Name = styled.div`
  font-weight: 800;
  font-size: 16px;
`;

const Meta = styled.div`
  font-size: 12.5px;
  color: var(--br-muted);
  margin-top: 4px;
`;

const DiscoverCard: React.FC<{ group: CritiquePublicGroup }> = ({ group }) => {
  const { t } = useTranslation();
  return (
    <Link
      className="br-card-link"
      data-testid={`discover-card-${group._id}`}
      to={`/critique-groups/${group._id}`}
    >
      <Card>
        <CardContent>
          <BookSpine
            title={group.book?.title}
            coverUrl={group.coverUrl || group.book?.coverUrl || undefined}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Name>
              {group.name || t('CritiqueUntitledGroup', 'Untitled group')}
            </Name>
            {group.host?.displayName && (
              <Meta>
                {t('CritiqueHostedBy', 'Hosted by {{name}}', {
                  name: group.host.displayName,
                })}
              </Meta>
            )}
            {group.book?.title && (
              <Meta>
                <Icon name="book" /> {group.book.title}
              </Meta>
            )}
            <Meta>
              <Icon name="users" />{' '}
              {t('CritiqueMemberCount', '{{count}} members', {
                count: group.memberCount,
              })}
              {group.isFull && ` · ${t('CritiqueFull', 'Full')}`}
            </Meta>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const CritiqueDiscover: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState('');
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCritiqueDiscover(search);

  // Flatten the accumulated pages into one list for the grid.
  const groups = (data?.pages || []).flatMap(p => p.groups);

  const runSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSearch(term.trim());
  };

  return (
    <div data-testid="critique-discover">
      <Header>
        <div>
          <Button
            size="small"
            variant="ghost"
            onClick={() => history.push('/critique-groups')}
            style={{ marginBottom: 10 }}
          >
            <Icon name="arrow left" />
            {t('CritiqueBackToGroups', 'My groups')}
          </Button>
          <h3 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>
            {t('CritiqueDiscoverTitle', 'Discover critique groups')}
          </h3>
          <p
            style={{
              margin: '4px 0 0',
              color: 'var(--br-muted)',
              fontSize: 14,
            }}
          >
            {t(
              'CritiqueDiscoverSubtitle',
              'Public groups you can ask to join. The host approves every request.'
            )}
          </p>
        </div>
        <SearchForm onSubmit={runSearch} role="search">
          <TextField
            id="critique-discover-search"
            label={t('CritiqueSearchGroups', 'Search groups')}
            value={term}
            onChange={event => setTerm(event.target.value)}
            data-testid="discover-search"
          />
          <Button type="submit" size="small">
            {t('Search', 'Search')}
          </Button>
        </SearchForm>
      </Header>

      {isLoading && <CritiqueLoadingState />}
      {isError && (
        <CritiqueErrorState
          message={error?.message}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && groups.length === 0 && (
        <div style={{ marginTop: 30 }}>
          <CritiqueEmptyState
            data-testid="discover-empty"
            icon="compass"
            title={
              search
                ? t('CritiqueDiscoverNoHits', 'No groups match your search')
                : t('CritiqueDiscoverEmptyTitle', 'No public groups yet')
            }
            body={t(
              'CritiqueDiscoverEmptyBody',
              'Public critique groups will show up here as authors open them to the community.'
            )}
          />
        </div>
      )}

      {!isLoading && !isError && groups.length > 0 && (
        <>
          <Grid>
            {groups.map(group => (
              <DiscoverCard key={group._id} group={group} />
            ))}
          </Grid>
          {hasNextPage && (
            <div style={{ marginTop: 20 }}>
              <LoadMore
                onLoadMore={() => {
                  if (!isFetchingNextPage) fetchNextPage();
                }}
                loading={isFetchingNextPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CritiqueDiscover;
